/**
 * OrderCardList — Shopee/Lazada seller-center style order cards.
 *
 * Layout per card:
 *   - Header: order ID + date/time + channel + status badge
 *   - Body (two columns):
 *     LEFT:  customer avatar + name, contact person, phone, shipping address
 *     RIGHT: product items with thumbnails
 *   - Footer: quick action buttons + total qty + grand total
 */

import { useMemo, type MouseEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Package, ImageIcon, User, MapPin, Phone, Truck, PackageCheck, Banknote, CreditCard, FileText, Printer, CheckCircle, Clock, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { getERPClient } from "@/lib/erp/client";
import { OrderStatusBadge } from "./OrderStatusBadge";
import { ChannelBadge } from "@/features/channels/components/ChannelBadge";
import { StoreBadge } from "@/components/storefront/StoreBadge";
import { useStoreList } from "@/hooks/useStoreList";
import { useLanguage } from "@/i18n/useLanguage";
import { useAuthStore } from "@/lib/store/authStore";
import type { PaymentSlip } from "@/features/storefront/payment-slips/hooks";
import type { SimpleTableStat } from "@/features/common/components/SimpleTableView";
import { useOrderItemsBatch, useCustomerAvatarsBatch, type OrderItemRow } from "../hooks/useOrderBatch";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type OrderRow = Record<string, unknown> & { name?: string };

interface OrderCardListProps {
  items: OrderRow[];
  isLoading: boolean;
  total: number;
  getRowId: (row: OrderRow) => string;
  onRowClick?: (id: string) => void;
  summaryStats?: SimpleTableStat[];
}

// ---------------------------------------------------------------------------
// Bank logo mapping — major Thai banks (mirrors PaymentSlipsCard)
// ---------------------------------------------------------------------------

const BANK_LOGO: Record<string, string> = {
  Kasikornbank: "/banks/KBANK.png",
  KBank: "/banks/KBANK.png",
  KBANK: "/banks/KBANK.png",
  "Bangkok Bank": "/banks/BBL.png",
  BBL: "/banks/BBL.png",
  SCB: "/banks/SCB.png",
  "Siam Commercial Bank": "/banks/SCB.png",
  "Krungthai Bank": "/banks/KTB.png",
  KTB: "/banks/KTB.png",
  Krungsri: "/banks/BAY.png",
  "Bank of Ayudhya": "/banks/BAY.png",
  BAY: "/banks/BAY.png",
  TTB: "/banks/TTB.png",
  TMBThanachart: "/banks/TTB.png",
  GSB: "/banks/GSB.png",
  "Government Savings Bank": "/banks/GSB.png",
};

// ---------------------------------------------------------------------------
// Hook: batch-fetch payment slips for visible orders
// ---------------------------------------------------------------------------

function usePaymentSlipsBatch(orderNames: string[]) {
  return useQuery({
    queryKey: ["payment-slips-batch", orderNames],
    queryFn: async () => {
      if (orderNames.length === 0) return {};
      const client = getERPClient();
      const rows = await client.list<PaymentSlip>({
        doctype: "Storefront Payment Slip",
        filters: [["sales_order", "in", orderNames]],
        fields: ["name", "sales_order", "status", "verified_amount", "sender_bank", "sender_name", "easyslip_status", "trans_ref", "slip_image", "uploaded_at", "creation"],
        order_by: "creation desc",
        limit_page_length: 200,
      });
      const map: Record<string, PaymentSlip[]> = {};
      for (const r of rows) {
        const so = String(r.sales_order ?? "");
        if (!so) continue;
        if (!map[so]) map[so] = [];
        map[so].push(r);
      }
      return map;
    },
    enabled: orderNames.length > 0,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STAT_BG = {
  default: "bg-neutral-50 dark:bg-neutral-800/60",
  green: "bg-emerald-50 dark:bg-emerald-900/20",
  orange: "bg-amber-50 dark:bg-amber-900/20",
  red: "bg-red-50 dark:bg-red-900/20",
} as const;

const STAT_DOT = {
  default: "bg-neutral-300 dark:bg-neutral-600",
  green: "bg-emerald-500 dark:bg-emerald-400",
  orange: "bg-amber-500 dark:bg-amber-400",
  red: "bg-red-500 dark:bg-red-400",
} as const;

const STAT_NUM = {
  default: "text-neutral-900 dark:text-neutral-100",
  green: "text-emerald-700 dark:text-emerald-400",
  orange: "text-amber-700 dark:text-amber-400",
  red: "text-red-700 dark:text-red-400",
} as const;

function formatCompactDate(dateStr: string, th: boolean): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return th ? "เมื่อสักครู่" : "Just now";
  if (diffMin < 60) return th ? `${diffMin} นาทีที่แล้ว` : `${diffMin}m ago`;
  if (diffHr < 24) return th ? `${diffHr} ชม.ที่แล้ว` : `${diffHr}h ago`;
  if (diffDay === 1) return th ? "เมื่อวาน" : "Yesterday";
  if (diffDay < 7) return th ? `${diffDay} วันที่แล้ว` : `${diffDay}d ago`;
  return date.toLocaleDateString(th ? "th-TH" : "en-US", { month: "short", day: "numeric" });
}

function formatOrderTime(dateStr: string): string {
  const date = new Date(dateStr);
  const h = date.getHours().toString().padStart(2, "0");
  const m = date.getMinutes().toString().padStart(2, "0");
  return `${h}:${m}`;
}

function formatCurrency(value: unknown): string {
  const num = Number(value ?? 0);
  return `\u0E3F${num.toLocaleString("th-TH", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function stripHtml(html: string): string {
  return html.replace(/<br\s*\/?>/gi, ", ").replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
}

// ---------------------------------------------------------------------------
// Derive quick actions for an order
// ---------------------------------------------------------------------------

interface QuickAction {
  id: string;
  label: string;
  icon: typeof Truck;
  variant: "primary" | "secondary";
  /** If set, navigate to this path instead of opening detail panel */
  href?: string;
}

function deriveQuickActions(order: OrderRow): QuickAction[] {
  const actions: QuickAction[] = [];
  const status = String(order.status ?? "");
  const docstatus = Number(order.docstatus ?? 0);
  const perDelivered = Number(order.per_delivered ?? 0);

  const paymentMethodType = String(order.payment_method_type ?? "");
  const grandTotal = Number(order.grand_total ?? 0);
  const advancePaid = Number(order.advance_paid ?? 0);
  const isFullyPaid = grandTotal > 0 && advancePaid >= grandTotal;
  const channel = String(order.source_channel ?? "");
  const isStorefront = channel === "Storefront";
  const orderId = String(order.name ?? "");

  const canShip =
    docstatus === 1 &&
    perDelivered < 100 &&
    ["To Deliver and Bill", "To Deliver"].includes(status);

  const canRecordCODPayment =
    isStorefront && paymentMethodType === "COD" && status === "To Bill";

  const canRecordManualPayment =
    docstatus === 1 &&
    ["To Deliver and Bill", "To Bill"].includes(status) &&
    !isFullyPaid &&
    !canRecordCODPayment;

  const showSlipsLink =
    isStorefront &&
    (paymentMethodType === "Bank Transfer" || paymentMethodType === "Event Checkout");

  if (canShip) {
    actions.push({ id: "ship", label: "ส่งของ", icon: Truck, variant: "primary" });
    actions.push({ id: "pick-pack", label: "Pick & Pack", icon: PackageCheck, variant: "secondary" });
  }

  if (canRecordCODPayment) {
    actions.push({ id: "cod-payment", label: "บันทึกการชำระ", icon: Banknote, variant: "secondary" });
  }

  if (canRecordManualPayment) {
    actions.push({ id: "manual-payment", label: "บันทึกการชำระ", icon: CreditCard, variant: "secondary" });
  }

  if (showSlipsLink) {
    actions.push({
      id: "slips",
      label: "สลิปชำระเงิน",
      icon: FileText,
      variant: "secondary",
      href: `/storefront/payment-slips?sales_order=${orderId}`,
    });
  }

  // Print Studio — always available for submitted orders
  if (docstatus === 1) {
    actions.push({
      id: "print-studio",
      label: "Print Studio",
      icon: Printer,
      variant: "secondary",
      href: `/print-studio/Sales Order/${encodeURIComponent(orderId)}`,
    });
  }

  return actions;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OrderCardList({
  items,
  isLoading,
  total: _total,
  getRowId,
  onRowClick,
  summaryStats,
}: OrderCardListProps) {
  const { language } = useLanguage();
  const th = language === "th";
  const tenantServerUrl = useAuthStore((s) => s.tenantServerUrl);

  // Multi-store: reveal which storefront each order came from (store name +
  // color badge) next to the channel chip. Only when the tenant has 2+ stores.
  const { data: storeList } = useStoreList();
  const isMultiStore = (storeList?.length ?? 0) > 1;


  const orderNames = useMemo(
    () => items.map((r) => String(r.name ?? "")).filter(Boolean),
    [items],
  );

  const { data: allItems } = useOrderItemsBatch(orderNames);

  const customerNames = useMemo(
    () => [...new Set(items.map((r) => String(r.customer ?? "")).filter(Boolean))],
    [items],
  );

  const { data: customerImages } = useCustomerAvatarsBatch(customerNames);
  const { data: slipsByOrder } = usePaymentSlipsBatch(orderNames);

  const itemsByOrder = useMemo(() => {
    const map = new Map<string, OrderItemRow[]>();
    for (const item of allItems ?? []) {
      const list = map.get(item.parent) ?? [];
      list.push(item);
      map.set(item.parent, list);
    }
    return map;
  }, [allItems]);

  // Loading skeleton
  if (isLoading && items.length === 0) {
    return (
      <div className="px-3 py-3 space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="rounded-xl border border-neutral-200 dark:border-neutral-800 p-4 animate-pulse">
            <div className="flex justify-between mb-4">
              <div className="h-3 w-40 rounded bg-neutral-100 dark:bg-neutral-800" />
              <div className="h-5 w-20 rounded-full bg-neutral-100 dark:bg-neutral-800" />
            </div>
            <div className="flex gap-6">
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-neutral-100 dark:bg-neutral-800" />
                  <div className="h-3 w-28 rounded bg-neutral-100 dark:bg-neutral-800" />
                </div>
                <div className="h-3 w-24 rounded bg-neutral-100 dark:bg-neutral-800" />
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-neutral-100 dark:bg-neutral-800" />
                  <div className="h-3 w-32 rounded bg-neutral-100 dark:bg-neutral-800" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex items-center justify-center py-20 text-neutral-400 dark:text-neutral-500 text-[13px]">
        {th ? "ไม่พบคำสั่งซื้อ" : "No orders found"}
      </div>
    );
  }

  return (
    <div className="px-3 py-2">
      {/* Summary stats bar */}
      {summaryStats && summaryStats.length > 0 && (
        <div className="flex items-center gap-2 px-1 py-2 mb-2">
          {summaryStats.map((stat, i) => {
            const c = stat.color ?? "default";
            return (
              <div key={i} className={cn("flex items-center gap-2 px-3 py-1.5 rounded-lg", STAT_BG[c])}>
                <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", STAT_DOT[c])} />
                <span className={cn("text-[15px] font-semibold tabular-nums", STAT_NUM[c])}>{stat.value}</span>
                <span className="text-[12px] text-neutral-500 dark:text-neutral-400">{stat.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Order cards */}
      <div className="space-y-4">
        {items.map((order) => {
          const rowId = getRowId(order);
          const orderId = String(order.name ?? "");
          const customer = String(order.customer_name ?? "");
          const customerKey = String(order.customer ?? "");
          const customerImg = customerImages?.[customerKey];
          const creationStr = order.creation ? String(order.creation) : "";
          const date = creationStr ? formatCompactDate(creationStr, th) : "";
          const time = creationStr ? formatOrderTime(creationStr) : "";
          const status = (order.status ?? order.docstatus) as string;
          const perDelivered = order.per_delivered != null ? Number(order.per_delivered) : undefined;
          const marketplaceStatus = (order.shopee_order_status ?? order.lazada_order_status ?? undefined) as string | undefined;
          const channel = String(order.source_channel ?? "storefront");
          const grandTotal = Number(order.grand_total ?? 0);
          const orderItems = itemsByOrder.get(orderId) ?? [];
          const totalQty = Number(order.total_qty ?? orderItems.reduce((s, i) => s + i.qty, 0));

          const contactPerson = String(order.contact_display ?? "");
          const contactMobile = String(order.contact_mobile ?? order.contact_phone ?? "");
          const shippingAddress = order.address_display ? stripHtml(String(order.address_display)) : "";

          const quickActions = deriveQuickActions(order);
          const orderSlips = slipsByOrder?.[orderId] ?? [];
          const verifiedSlip = orderSlips.find((s) => s.status === "Verified");
          const latestSlip = orderSlips[0];

          /** Stop propagation so action clicks don't trigger card's onRowClick */
          const stopProp = (e: MouseEvent) => e.stopPropagation();

          return (
            <div
              key={rowId}
              onClick={() => onRowClick?.(rowId)}
              className={cn(
                "rounded-xl border border-neutral-200 dark:border-neutral-800",
                "bg-white dark:bg-neutral-950",
                "transition-all duration-150",
                onRowClick && "cursor-pointer hover:border-neutral-300 dark:hover:border-neutral-700 hover:shadow-sm",
              )}
            >
              {/* ── Header ── */}
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-neutral-100 dark:border-neutral-800/60 gap-2">
                <div className="flex items-center gap-2 text-[12px] text-muted-foreground min-w-0 flex-wrap">
                  <span className="font-mono font-medium text-foreground/70">{orderId}</span>
                  <span className="text-neutral-300 dark:text-neutral-700 hidden sm:inline">|</span>
                  <span>{date}</span>
                  {time && <span className="text-neutral-400 dark:text-neutral-600 hidden sm:inline">{time}</span>}
                  <ChannelBadge channel={channel} />
                  {isMultiStore && !!order.custom_storefront_source && (
                    <StoreBadge storefrontKey={String(order.custom_storefront_source)} showLabel />
                  )}
                </div>
                <OrderStatusBadge status={status} perDelivered={perDelivered} marketplaceStatus={marketplaceStatus} paymentVerified={!!verifiedSlip || (grandTotal > 0 && Number(order.advance_paid ?? 0) >= grandTotal)} />
              </div>

              {/* ── Body: stacked on mobile, two-column on md+ ── */}
              <div className="flex flex-col md:flex-row md:divide-x divide-neutral-100 dark:divide-neutral-800/60">

                {/* LEFT column — Customer & shipping */}
                <div className="w-full md:w-[280px] shrink-0 px-4 py-3 space-y-2.5 border-b md:border-b-0 border-neutral-100 dark:border-neutral-800/60">
                  <div className="flex items-center gap-2.5">
                    {customerImg ? (
                      <img
                        src={customerImg}
                        alt=""
                        className="w-8 h-8 rounded-full object-cover bg-neutral-100 dark:bg-neutral-800 shrink-0"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center shrink-0">
                        <User className="w-4 h-4 text-neutral-400 dark:text-neutral-600" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-foreground truncate leading-tight">{customer}</p>
                      {contactPerson && contactPerson !== customer && (
                        <p className="text-[11px] text-muted-foreground truncate mt-0.5">{contactPerson}</p>
                      )}
                    </div>
                  </div>

                  {contactMobile && (
                    <div className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                      <Phone className="w-3 h-3 text-neutral-400 shrink-0" />
                      <span>{contactMobile}</span>
                    </div>
                  )}

                  {shippingAddress && (
                    <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground leading-relaxed">
                      <MapPin className="w-3 h-3 text-neutral-400 shrink-0 mt-0.5" />
                      <span className="line-clamp-3">{shippingAddress}</span>
                    </div>
                  )}
                </div>

                {/* RIGHT column — Product items */}
                <div className="flex-1 px-4 py-3">
                  {orderItems.length > 0 ? (
                    <div className="space-y-2">
                      {orderItems.slice(0, 3).map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          {item.image ? (
                            <img
                              src={item.image}
                              alt=""
                              className="w-10 h-10 rounded-xs object-cover bg-neutral-100 dark:bg-neutral-800 shrink-0"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-xs bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center shrink-0">
                              <ImageIcon className="w-4 h-4 text-neutral-300 dark:text-neutral-600" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] text-foreground truncate leading-tight">{item.item_name}</p>
                            <p className="text-[11px] text-muted-foreground mt-0.5">
                              {item.item_code} &times; {item.qty}
                            </p>
                          </div>
                          <span className="text-[13px] tabular-nums text-muted-foreground shrink-0">
                            {formatCurrency(item.amount)}
                          </span>
                        </div>
                      ))}
                      {orderItems.length > 3 && (
                        <p className="text-[11px] text-muted-foreground pl-[52px]">
                          +{orderItems.length - 3} {th ? "รายการเพิ่มเติม" : "more items"}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Package className="w-4 h-4 opacity-50" />
                      <span className="text-[13px]">
                        {totalQty} {th ? "รายการ" : "items"}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Payment slip summary ── */}
              {latestSlip && (() => {
                const slip = verifiedSlip ?? latestSlip;
                const bankLogo = slip.sender_bank ? BANK_LOGO[slip.sender_bank] : undefined;
                const slipImgUrl = slip.slip_image
                  ? slip.slip_image.startsWith("/") ? `${tenantServerUrl}${slip.slip_image}` : slip.slip_image
                  : undefined;
                return (
                  <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-t border-neutral-100 dark:border-neutral-800/60 bg-neutral-50/30 dark:bg-neutral-900/20">
                    {/* Slip thumbnail */}
                    {slipImgUrl && (
                      <img
                        src={slipImgUrl}
                        alt="slip"
                        className="w-8 h-10 rounded-sm object-cover border border-neutral-200 dark:border-neutral-700 flex-shrink-0"
                      />
                    )}
                    {/* Status badge */}
                    {verifiedSlip ? (
                      <span className="flex items-center gap-1 text-[11px] font-medium text-green-600 dark:text-green-400">
                        <CheckCircle className="w-3.5 h-3.5" />
                        {th ? "ยืนยันแล้ว" : "Verified"}
                      </span>
                    ) : latestSlip.status === "Pending" || latestSlip.status === "Uploaded" ? (
                      <span className="flex items-center gap-1 text-[11px] font-medium text-amber-600 dark:text-amber-400">
                        <Clock className="w-3.5 h-3.5" />
                        {th ? "รอตรวจสอบ" : "Pending"}
                      </span>
                    ) : null}
                    {/* Amount */}
                    {slip.verified_amount != null && slip.verified_amount! > 0 && (
                      <span className="text-[12px] font-semibold tabular-nums text-foreground">
                        {formatCurrency(slip.verified_amount!)}
                      </span>
                    )}
                    {/* Bank logo + name */}
                    {slip.sender_bank && (
                      <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                        {bankLogo && (
                          <img src={bankLogo} alt={slip.sender_bank} className="w-4 h-4 rounded-full object-contain" />
                        )}
                        {slip.sender_bank}
                      </span>
                    )}
                    {/* Sender name */}
                    {slip.sender_name && (
                      <span className="text-[11px] text-muted-foreground truncate max-w-[140px]">
                        {slip.sender_name}
                      </span>
                    )}
                    {/* Bank-verified badge */}
                    {slip.easyslip_status === "Auto-Verified" && (
                      <span className="flex items-center gap-0.5 text-[10px] text-emerald-600 dark:text-emerald-400">
                        <ShieldCheck className="w-3 h-3" />
                        {th ? "ธนาคาร" : "Bank"}
                      </span>
                    )}
                    {/* Transaction reference — hidden on mobile */}
                    {slip.trans_ref && (
                      <span className="text-[10px] font-mono text-muted-foreground/60 truncate max-w-[120px] hidden sm:inline">
                        ref: {slip.trans_ref}
                      </span>
                    )}
                  </div>
                );
              })()}

              {/* ── Footer: actions + total ── */}
              <div className="flex flex-wrap items-center justify-between px-4 py-2 border-t border-neutral-100 dark:border-neutral-800/60 bg-neutral-50/50 dark:bg-neutral-900/30 rounded-b-xl gap-2 md:gap-3">
                {/* Quick action buttons */}
                <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-x-auto" onClick={stopProp}>
                  {quickActions.map((action) => {
                    const Icon = action.icon;
                    if (action.href) {
                      return (
                        <Link
                          key={action.id}
                          to={action.href}
                          onClick={stopProp}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-[11px] font-medium text-muted-foreground hover:text-foreground hover:border-neutral-300 dark:hover:border-neutral-600 transition-colors shrink-0"
                        >
                          <Icon className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">{action.label}</span>
                        </Link>
                      );
                    }
                    return (
                      <button
                        key={action.id}
                        type="button"
                        onClick={(e) => {
                          stopProp(e);
                          // Open the order detail panel — the full action dialogs live there
                          onRowClick?.(rowId);
                        }}
                        className={cn(
                          "flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-colors shrink-0",
                          action.variant === "primary"
                            ? "bg-primary text-primary-foreground hover:bg-primary/90"
                            : "border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-muted-foreground hover:text-foreground hover:border-neutral-300 dark:hover:border-neutral-600",
                        )}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        <span>{action.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Qty + total */}
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-[12px] text-muted-foreground">
                    {totalQty} {th ? "รายการ" : "items"}
                  </span>
                  <span className="text-[15px] font-semibold tabular-nums">
                    {formatCurrency(grandTotal)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
